import { useQuery } from "@tanstack/react-query";

import { useGenerationSettings } from "~/entities/model";
import { ApiError, apiPostV2 } from "~/shared/api/client";
import { findModeConfig, type GenerationMode, type ModelConfig } from "~/shared/data/chat-models";
import { useChatSettingsStore } from "~/shared/store/useChatSettingsStore";

interface PriceQuoteResponse {
    unitPriceUsd: string;
    totalPriceUsd: string;
    currency: string;
    batchSize: number;
}

/**
 * Машинный код отказа: набранный состав входов не исполняет ни один активный
 * провайдер. Отличается от «цены на комбинацию нет вовсе» — первое человек может
 * исправить сам, второе объяснять ему нечем.
 */
export const INPUTS_NOT_EXECUTABLE_CODE = "inputs_not_executable";

/** Отказ цены — из-за набора входов, а не из-за незаполненного каталога? */
export function isInputsNotExecutable(error: unknown): boolean {
    return error instanceof ApiError && error.code === INPUTS_NOT_EXECUTABLE_CODE;
}

/**
 * Состав каналов входа, набранный в баре.
 *
 * Цену считает тот же гейт фич, что выбирает исполнителя, поэтому состав входов
 * обязан уезжать вместе с комбинацией: без него бэкенд не знает, что запрос
 * требует референсов или пары кадров, и вернёт цену набора, который никто не
 * исполнит.
 */
export interface QuoteInputsSummary {
    readonly inputImageCount: number;
    readonly hasLastFrame: boolean;
    readonly referenceImageCount: number;
    readonly elementCount: number;
    readonly inputVideoCount: number;
    /**
     * Суммарная длина приложенных видео, как её измерил браузер (без округления).
     *
     * Единственный признак входа, который меняет САМУ цену, а не выбор
     * исполнителя: у тарифов, где вход оплачивается вместе с выходом, без него
     * бар показывал цену одного выхода, а списывалось за вход и выход.
     * Округляет сервер — тем же правилом, что и его probe.
     */
    readonly inputVideoDurationSeconds: number | null;
    /**
     * Длина промпта в символах (сам текст серверу не нужен — на цену влияет
     * только длина).
     *
     * У строки каталога свой потолок промпта, и исполнителя отбирают по нему:
     * без этого признака бар показывал цену провайдера, который длинный промпт
     * не принимает, а списывалось по фактическому — вдвое дороже.
     */
    readonly promptLength: number;
}

export const EMPTY_QUOTE_INPUTS: QuoteInputsSummary = {
    inputImageCount: 0,
    hasLastFrame: false,
    referenceImageCount: 0,
    elementCount: 0,
    inputVideoCount: 0,
    inputVideoDurationSeconds: null,
    promptLength: 0,
};

/** Шаг огрубления длины промпта: цена перезапрашивается раз в сотню символов. */
const PROMPT_LENGTH_STEP = 100;

/**
 * Длина промпта для запроса цены — округлённая ВВЕРХ до сотни.
 *
 * Точная длина меняется на каждый символ и дёргала бы цену на каждое нажатие
 * клавиши. Огрубление вверх безопасно: оно может только раньше отбросить
 * провайдера с близким потолком (покажем цену подороже), но никогда не покажет
 * цену того, кто промпт уже не примет.
 */
export function quotePromptLength(prompt: string): number {
    return Math.ceil(prompt.length / PROMPT_LENGTH_STEP) * PROMPT_LENGTH_STEP;
}

export interface PriceQuoteRequest {
    model_name: string;
    generation_mode: string;
    resolution: number | null;
    aspect_ratio: string | null;
    duration_sec: number | null;
    batch_size: number;
    input_image_count: number;
    has_last_frame: boolean;
    reference_image_count: number;
    element_count: number;
    input_video_count: number;
    input_video_duration_seconds: number | null;
    prompt_length: number;
    with_sound: boolean | null;
    upscale_factor: number | null;
}

export interface PriceQuotePayloadInput {
    model: ModelConfig;
    mode: GenerationMode;
    aspectRatio: string;
    resolution: number;
    duration: number;
    batchSize: number;
    inputs: QuoteInputsSummary;
    audioEnabled: boolean;
    upscaleFactor: number | null;
}

export function buildPriceQuotePayload({
    model,
    mode,
    aspectRatio,
    resolution,
    duration,
    batchSize,
    inputs,
    audioEnabled,
    upscaleFactor,
}: PriceQuotePayloadInput): PriceQuoteRequest | null {
    // Не отправляем запрос при рассогласованном state (например, после смены
    // Artifact Type, пока SettingsPanel не синхронизировал выбор модели/режима).
    const modeConfig = findModeConfig(model, mode);
    if (!modeConfig) return null;
    // Режимы без соотношения сторон (omni / motion control — ориентация по
    // референсам) не проверяют aspect: store держит stale-значение прошлого
    // режима ("16:9"), которого нет в пустом списке — иначе quote не уходит и
    // цена на кнопке не считается.
    const aspectApplies = modeConfig.aspectRatios.length > 0;
    if (aspectApplies && !modeConfig.aspectRatios.includes(aspectRatio)) return null;
    // Разрешение — такая же необязательная ось, как соотношение и длительность:
    // у Nano Banana первой версии размер задаёт только соотношение сторон. Без
    // этой проверки store отдавал бы разрешение прошлой модели, цена по нему не
    // находится, и на кнопке застывала бы сумма от предыдущего выбора.
    const resolutionApplies = modeConfig.resolutions.length > 0;
    if (resolutionApplies && !modeConfig.resolutions.includes(resolution)) return null;
    const durationApplies = modeConfig.durations.length > 0;
    if (durationApplies && !modeConfig.durations.includes(duration)) return null;

    return {
        model_name: model.id,
        generation_mode: mode,
        resolution: resolutionApplies ? resolution : null,
        aspect_ratio: aspectApplies ? aspectRatio || null : null,
        duration_sec: durationApplies ? duration : null,
        batch_size: batchSize,
        input_image_count: inputs.inputImageCount,
        has_last_frame: inputs.hasLastFrame,
        reference_image_count: inputs.referenceImageCount,
        element_count: inputs.elementCount,
        input_video_count: inputs.inputVideoCount,
        input_video_duration_seconds: inputs.inputVideoDurationSeconds,
        prompt_length: inputs.promptLength,
        // У строки со звуком и без цена разная, поэтому выбор звука — ось
        // прайсинга. Режим без звуковой оси ось не сужает (null), иначе цена
        // искалась бы среди строк, которых у такой модели нет.
        with_sound: modeConfig.audioSupport === "none" ? null : audioEnabled,
        // Кратность апскейла — ось прайсинга: у Topaz строка ×4 стоит дороже
        // ×2. Режим без этой оси её не сужает (null).
        upscale_factor: modeConfig.upscaleFactors.length > 0 ? upscaleFactor : null,
    };
}

export function useQuotePrice(inputs: QuoteInputsSummary = EMPTY_QUOTE_INPUTS) {
    const modelId = useChatSettingsStore((s) => s.modelId);
    const mode = useChatSettingsStore((s) => s.mode);
    const aspectRatio = useChatSettingsStore((s) => s.aspectRatio);
    const resolution = useChatSettingsStore((s) => s.resolution);
    const duration = useChatSettingsStore((s) => s.duration);
    const batchSize = useChatSettingsStore((s) => s.batchSize);
    const audioEnabled = useChatSettingsStore((s) => s.audioEnabled);
    const upscaleFactor = useChatSettingsStore((s) => s.upscaleFactor);

    const settings = useGenerationSettings();
    const model = settings.data?.find((m) => m.id === modelId) ?? null;
    const payload = model
        ? buildPriceQuotePayload({
              model,
              mode,
              aspectRatio,
              resolution,
              duration,
              batchSize,
              inputs,
              audioEnabled,
              upscaleFactor,
          })
        : null;

    return useQuery<PriceQuoteResponse>({
        queryKey: ["v2", "generation", "price", payload],
        queryFn: () => apiPostV2<PriceQuoteResponse>("/generation/price", payload),
        enabled: payload != null,
        staleTime: 30_000,
        retry: false,
    });
}
